import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  Col,
  Row,
  Form,
  Button,
  Label,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as XLSX from "xlsx";
import { armarPlantillaOC, leerExcelOC } from "../OrdenesCompra/plantillaOC";

// Compra sin orden de compra ni embarque. Se guarda en una sola peticion
// (POST /comprasDirecta): Entrada + Nota CxP + inventario + acumulado + ultimo costo.

const renglonVacio = () => ({
  id: uuidv4(),
  articulos: "",
  articulosNombre: null,
  cantidad: 0,
  precio: 0,
  total: 0,
});

const formato2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ComprasDirectasCreate() {
  const { user } = useContext(AuthContext);
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const URL_COMPRAS = process.env.REACT_APP_URL_COMPRAS;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;
  const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  let hoy = moment(new Date()).format("YYYY-MM-DD");

  const [modalProgreso, setModalProgreso] = useState(false);
  const toggleProgreso = () => setModalProgreso(!modalProgreso);

  const [fecha, setFecha] = useState(hoy);
  const [factura, setFactura] = useState("NA");
  const [observaciones, setObservaciones] = useState("NA");
  const [reciboDeposito, setReciboDeposito] = useState("NA");
  const [tipoCambio, setTipoCambio] = useState(1);
  const [moneda, setMoneda] = useState("MXN");
  const [articulos, setArticulos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [inputFields, setInputFields] = useState([renglonVacio()]);
  const [validaBoton, setValidaBoton] = useState(true);

  // Proveedores en pesos: tipo de cambio 1 y precio sugerido = costoMX
  const esPesos = !moneda || moneda === "MXN" || moneda === "MX";
  // Un master no guarda inventario: entra a su variante (la que comparte SKU o la unica) o, si tiene
  // varias presentaciones, a la mas chica con la cantidad en unidades base. La conversion la hace el
  // back (POST /comprasDirecta); aqui solo se muestra a donde va.
  const destinoMaster = (m) => {
    const hijos = articulos.filter(
      (h) => String(h.articuloPadre) === String(m._id) && h.modelo !== "MASTER" && h.is_active !== "No"
    );
    if (!hijos.length) return null;
    const mismo = hijos.filter((h) => String(h.SKU).toUpperCase() === String(m.SKU).toUpperCase());
    if (mismo.length === 1) return { variante: mismo[0], factor: 1, varias: false };
    if (hijos.length === 1) return { variante: hijos[0], factor: 1, varias: false };
    const factor = Math.min(...hijos.map((h) => Number(h.piezasPorCaja) || 1));
    const cands = hijos.filter((h) => (Number(h.piezasPorCaja) || 1) === factor);
    return { variante: cands.length === 1 ? cands[0] : null, factor, varias: true, base: cands[0] };
  };

  // Precio sugerido: el costo de la ficha en la moneda del proveedor (sin costo en esa moneda = 0).
  // De un master, el de su variante (por unidad base si son presentaciones)
  const precioSugerido = (art) => {
    if (art.modelo === "MASTER") {
      const d = destinoMaster(art);
      const v = d && (d.variante || d.base);
      return v ? Number((precioSugerido(v) / (d.factor || 1)).toFixed(4)) : 0;
    }
    return esPesos ? Number(art.costoMX) || 0 : moneda === "USD" ? Number(art.costoUSD) || 0 : 0;
  };

  useEffect(() => {
    axios
      .get(URL_AREAS, { headers })
      .then((res) => setAreas(res.data))
      .catch((err) => console.log(err));

    axios
      .get(URL_PROVEEDORES, { headers })
      .then((res) => setProveedores(res.data))
      .catch((err) => console.log(err));

    axios
      .get(URL_ARTICULOS, { headers })
      .then((res) => setArticulos(res.data))
      .catch((err) => console.log(err));
  }, []);

  const total_cantidad = inputFields.reduce((t, a) => t + (parseFloat(a.cantidad) || 0), 0);
  const total_general = inputFields.reduce((t, a) => t + (parseFloat(a.total) || 0), 0);

  function jalaProveedor(id) {
    setSelectedProveedor(id);
    setInputFields([renglonVacio()]);
    const p = proveedores.find((a) => a._id === id);
    const m = (p && p.moneda) || "MXN";
    setMoneda(m);
    setTipoCambio(m === "MXN" || m === "MX" ? 1 : "");
  }

  const saveCompraNueva = async (event) => {
    event.preventDefault();

    const renglones = inputFields.filter((a) => a.articulos !== "" && parseFloat(a.cantidad) > 0);
    if (!selectedProveedor || !selectedArea || !fecha || !observaciones) {
      return Swal.fire({ icon: "error", title: "Faltan datos", text: "Captura fecha, proveedor, almacén y observaciones." });
    }
    if (!(parseFloat(tipoCambio) > 0)) {
      return Swal.fire({ icon: "error", title: "Falta el tipo de cambio", text: `Captura el tipo de cambio de ${moneda} a pesos.` });
    }
    if (!renglones.length) {
      return Swal.fire({ icon: "error", title: "Sin artículos", text: "Agrega al menos un artículo con cantidad." });
    }

    // Un renglon sin precio deja la cuenta por pagar al proveedor en $0
    const sinPrecio = renglones.filter((a) => !(parseFloat(a.precio) > 0));
    if (sinPrecio.length) {
      const r = await Swal.fire({
        icon: "warning",
        title: "Hay renglones sin precio",
        text: `${sinPrecio.length} renglón(es) tienen precio 0. La cuenta por pagar al proveedor quedaria en $0 por esas piezas. ¿Guardar asi?`,
        showCancelButton: true,
        confirmButtonText: "Si, guardar en 0",
        cancelButtonText: "Corregir",
        confirmButtonColor: "#d33",
      });
      if (!r.isConfirmed) return;
    }

    setValidaBoton(false);
    toggleProgreso();
    try {
      const { data } = await axios.post(
        `${URL_COMPRAS}Directa`,
        {
          fecha,
          proveedores: selectedProveedor,
          areas: selectedArea,
          factura,
          observaciones,
          reciboDeposito,
          tipoCambio: parseFloat(tipoCambio),
          user: user.id,
          articulos: renglones.map((a) => ({
            articulos: a.articulos,
            cantidad: parseFloat(a.cantidad),
            precio: parseFloat(a.precio) || 0,
          })),
        },
        { headers }
      );
      await axios.post(
        URL_LOGS,
        {
          tipo: "Crear Compra Directa",
          detalle: `Entrada ${data.compra.idCompras} / Proveedor: ${selectedProveedor}`,
          user: user.id,
        },
        { headers }
      );
      const conv = (data.conversiones || [])
        .map((c) => `${c.master} → ${c.cantidad} ${c.variante}${c.factor !== 1 ? ` (x${c.factor})` : ""}${c.creada ? " (variante nueva)" : ""}`)
        .join("<br>");
      Swal.fire({
        icon: "success",
        title: "Good job!",
        html: `Compra directa #${data.compra.idCompras} creada con exito${conv ? `<p style="text-align:left;margin-top:10px"><b>Masters que entraron a su variante:</b><br>${conv}</p>` : ""}`,
      });
      setTimeout(() => {
        window.location.reload();
      }, (data.conversiones || []).length ? 4000 : 1000);
    } catch (error) {
      setModalProgreso(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "No se guardo la compra",
        footer: `${error?.response?.data?.validation?.body?.message || error?.response?.data?.message || error.message}`,
      });
      console.log(error);
      setValidaBoton(true);
    }
  };

  function BuscaArticulo(id, selectedArticulo) {
    const newInputFields = inputFields.map((i) => {
      if (id === i.id) {
        if (!selectedArticulo) return { ...renglonVacio(), id: i.id };
        // No pisar un precio que el usuario ya capturo
        const precio = Number(i.precio) > 0 ? i.precio : precioSugerido(selectedArticulo);
        return {
          ...i,
          articulos: selectedArticulo._id,
          articulosNombre: selectedArticulo,
          precio,
          total: (parseFloat(i.cantidad) || 0) * precio,
        };
      }
      return i;
    });
    setInputFields(newInputFields);
  }

  const handleChangeInput = (id, event) => {
    const newInputFields = inputFields.map((i) => {
      if (id === i.id) {
        const r = { ...i, [event.target.name]: event.target.value };
        r.total = (parseFloat(r.cantidad) || 0) * (parseFloat(r.precio) || 0);
        return r;
      }
      return i;
    });
    setInputFields(newInputFields);
  };

  // ---- Carga masiva de renglones desde Excel (misma plantilla que la OC: SKU, CANTIDAD, PRECIO) ----
  const descargarPlantilla = () => {
    XLSX.writeFile(armarPlantillaOC("COMPRA"), "Plantilla Compra Directa.xlsx");
  };

  const importarExcel = (event) => {
    const archivo = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!archivo) return;
    if (!selectedProveedor) {
      Swal.fire({
        icon: "warning",
        title: "Primero elige el proveedor",
        text: "El precio por default depende de la moneda del proveedor.",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const { error, renglones, noEncontrados, invalidos } = leerExcelOC(wb, articulos, moneda, precioSugerido, { aceptarMasters: true });
      if (error) {
        Swal.fire({ icon: "error", title: "Archivo no válido", text: error });
        return;
      }
      const lista = (titulo, items) =>
        items.length
          ? `<p style="text-align:left;margin:8px 0 0"><b>${titulo} (${items.length})</b><br/>${items
              .slice(0, 25)
              .join("<br/>")}${items.length > 25 ? "<br/>..." : ""}</p>`
          : "";
      if (!renglones.length) {
        Swal.fire({
          icon: "warning",
          title: "No se cargó ningún renglón",
          html: lista("SKU no encontrados", noEncontrados) + lista("Renglones con datos inválidos", invalidos),
        });
        return;
      }

      // Se agregan a lo ya capturado (los renglones vacios se descartan)
      const nuevos = renglones.map((r) => ({ id: uuidv4(), ...r }));
      setInputFields([...inputFields.filter((r) => r.articulos !== ""), ...nuevos]);

      const piezas = nuevos.reduce((t, r) => t + r.cantidad, 0);
      const importe = nuevos.reduce((t, r) => t + r.total, 0);
      Swal.fire({
        icon: noEncontrados.length || invalidos.length ? "warning" : "success",
        title: `${nuevos.length} renglones cargados`,
        html:
          `${formato2.format(piezas)} pzas · $${formato2.format(importe)} ${moneda}<br/>Revísalos antes de guardar.` +
          lista("SKU no encontrados (no se cargaron)", noEncontrados) +
          lista("Renglones con datos inválidos (no se cargaron)", invalidos),
      });
    };
    reader.readAsArrayBuffer(archivo);
  };

  const handleAddFields = () => {
    setInputFields([...inputFields, renglonVacio()]);
  };

  const handleRemoveFields = (id) => {
    setInputFields(inputFields.filter((value) => value.id !== id));
  };

  // Tambien masters: el back los mete a su variante (ver destinoMaster)
  const options = articulos
    .filter((a) => a.is_active !== "No")
    .map((option) => {
      const junta = `${option.SKU || ""} ${option.nombre || ""} ${option.codigo || ""}`;
      const firstLetter = ((option.SKU || "X")[0] || "X").toUpperCase();
      return {
        firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
        ...option,
        junta,
      };
    })
    .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card container col-10">
          <h3 align="center">Nueva Compra Directa</h3>
          <p align="center" style={{ fontSize: "0.8rem", color: "#64748b" }}>
            Compra sin orden de compra ni embarque: entra al almacén, genera la cuenta por pagar al proveedor y actualiza el costo.
          </p>
          <Form onSubmit={saveCompraNueva}>
            <Row>
              <Col md={2}>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  placeholder="Fecha"
                  value={fecha}
                  required
                  onChange={(e) => {
                    setFecha(e.target.value);
                  }}
                />
              </Col>
              <Col md={3}>
                <Label className="mr-sm-2">Proveedor</Label>
                <Input
                  className="col-sm-12"
                  type="select"
                  value={selectedProveedor}
                  required
                  onChange={(e) => jalaProveedor(e.target.value)}
                >
                  <option value="">Selecciona</option>
                  {proveedores
                    .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                    .map((a) => {
                      return (
                        <option key={a._id} value={a._id}>
                          {a.nombre_comercial} {a.moneda}
                        </option>
                      );
                    })}
                </Input>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Almacen</Label>
                <Input
                  className="col-sm-12"
                  type="select"
                  value={selectedArea}
                  required
                  onChange={(e) => {
                    setSelectedArea(e.target.value);
                  }}
                >
                  <option value="">Selecciona</option>
                  {areas
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
                    .map((a) => {
                      return (
                        <option key={a._id} value={a._id}>
                          {a.name}
                        </option>
                      );
                    })}
                </Input>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Tipo Cambio {moneda} → MXN</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Tipo Cambio"
                  value={tipoCambio}
                  required
                  disabled={esPesos}
                  onChange={(e) => {
                    setTipoCambio(e.target.value);
                  }}
                />
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Factura</Label>
                <Input
                  type="text"
                  placeholder="Factura"
                  value={factura}
                  required
                  onChange={(e) => {
                    setFactura(e.target.value);
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <Label className="mr-sm-2">Observaciones</Label>
                <Input
                  type="text"
                  placeholder="Observaciones"
                  value={observaciones}
                  required
                  onChange={(e) => {
                    setObservaciones(e.target.value);
                  }}
                />
              </Col>
              <Col md={2}>
                <Label>Recibo Deposito</Label>
                <Input
                  type="text"
                  placeholder="Recibo Deposito"
                  value={reciboDeposito}
                  required
                  onChange={(e) => {
                    setReciboDeposito(e.target.value);
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <h4 id="logoutBoton">
                  TOTALES {formato2.format(total_cantidad)} pzas. / {"$" + formato2.format(total_general)} {moneda}
                </h4>
              </Col>
            </Row>

            <Row style={{ marginBottom: 10, alignItems: "flex-end" }}>
              <Col md={3}>
                <Button size="sm" color="success" onClick={descargarPlantilla}>
                  <i className="fas fa-file-excel"></i> Descargar plantilla
                </Button>
              </Col>
              <Col md={5}>
                <Label style={{ fontSize: "0.8rem", color: "#64748b", marginBottom: 2 }}>
                  Cargar renglones desde Excel (SKU, CANTIDAD, PRECIO)
                </Label>
                <Input type="file" accept=".xlsx,.xls" onChange={importarExcel} />
              </Col>
            </Row>

            {/* Tabla Articulos */}
            <Row>
              <Col md={5}>
                <Label className="mr-sm-2">Articulo</Label>
              </Col>
              <Col md={1}>
                <Label className="mr-sm-2">Cantidad</Label>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Precio {moneda}</Label>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Total</Label>
              </Col>
            </Row>

            {inputFields.map((inputField) => (
              <div key={inputField.id}>
                <Row>
                  <Col md={5}>
                    <Autocomplete
                      size="small"
                      value={inputField.articulosNombre}
                      onChange={(event, selectedArticulo) => {
                        BuscaArticulo(inputField.id, selectedArticulo);
                      }}
                      options={options}
                      groupBy={(option) => option.firstLetter}
                      getOptionLabel={(option) => option.junta}
                      getOptionSelected={(option, value) => option._id === value._id}
                      renderInput={(params) => (
                        <TextField {...params} label="Selecciona" variant="outlined" />
                      )}
                      renderOption={(option) => (
                        <React.Fragment>
                          {option.modelo === "MASTER" && (
                            <span
                              style={{
                                background: "#ec4899",
                                color: "#fff",
                                fontSize: "0.65rem",
                                padding: "2px 6px",
                                borderRadius: 4,
                                marginRight: 6,
                                fontWeight: 700,
                              }}
                            >
                              MASTER
                            </span>
                          )}
                          {option.articuloPadre && (
                            <span
                              style={{
                                background: "#e2e8f0",
                                color: "#64748b",
                                fontSize: "0.65rem",
                                padding: "2px 6px",
                                borderRadius: 4,
                                marginRight: 6,
                              }}
                            >
                              variante
                            </span>
                          )}
                          {option.SKU} {option.nombre}
                        </React.Fragment>
                      )}
                    />
                    {inputField.articulosNombre && inputField.articulosNombre.modelo === "MASTER" && (() => {
                      const d = destinoMaster(inputField.articulosNombre);
                      return (
                        <small style={{ color: d ? "#9d174d" : "#92400e", fontSize: "0.72rem", fontWeight: 600 }}>
                          {!d
                            ? "Sin variantes: al guardar se le crea su variante única (mismo SKU)"
                            : d.varias
                            ? `Entra a la presentación más chica (x${d.factor}${d.variante ? " · " + d.variante.SKU : ""}): captura la cantidad y el precio por unidad base`
                            : `Entra a su variante ${d.variante.SKU}`}
                        </small>
                      );
                    })()}
                  </Col>
                  <Col md={1}>
                    <Input
                      name="cantidad"
                      type="number"
                      min="0"
                      step="any"
                      placeholder="Cantidad"
                      value={inputField.cantidad}
                      onChange={(event) => handleChangeInput(inputField.id, event)}
                    />
                  </Col>
                  <Col md={2}>
                    <Input
                      name="precio"
                      type="number"
                      min="0"
                      step="any"
                      placeholder="Precio"
                      value={inputField.precio}
                      onChange={(event) => handleChangeInput(inputField.id, event)}
                    />
                  </Col>
                  <Col md={2}>
                    <Input
                      name="total"
                      type="text"
                      value={formato2.format(inputField.total || 0)}
                      disabled
                    />
                  </Col>
                  <Col>
                    <Button
                      size="sm"
                      className="btn"
                      color="danger"
                      disabled={inputFields.length === 1}
                      onClick={() => handleRemoveFields(inputField.id)}
                      tabIndex="-1"
                    >
                      <i className="fas fa-minus"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="info"
                      onClick={handleAddFields}
                      tabIndex="-1"
                    >
                      <i className="fas fa-plus"></i>
                    </Button>
                  </Col>
                </Row>
              </div>
            ))}
            <Row>
              <Col md={12}>
                <h4 id="logoutBoton">
                  TOTALES {formato2.format(total_cantidad)} pzas. / {"$" + formato2.format(total_general)} {moneda}
                </h4>
              </Col>
            </Row>
            {/* Termina Tabla Articulos */}

            <br />
            <Button type="submit" className="btn btn-success" disabled={!validaBoton}>
              Guardar
            </Button>
            <Button href="/ListadoCompras" className="btn btn-danger" id="botonListado">
              Regresar
            </Button>
          </Form>
          <br />
          <Modal size="sm" isOpen={modalProgreso} toggle={toggleProgreso}>
            <ModalHeader toggle={toggleProgreso}>
              <h4>Progreso</h4>
            </ModalHeader>
            <ModalBody>
              <h4>
                Este proceso puede tardar varios segundos.
                <br />
                Por favor no cierre ni refresque su navegador.
              </h4>
              <br />
              <div className="progreso">
                <Spinner style={{ width: "50px", height: "50px", color: "#232B41" }} />
                <Spinner style={{ width: "50px", height: "50px", color: "#232B41" }} />
                <Spinner style={{ width: "50px", height: "50px", color: "#232B41" }} />
              </div>
            </ModalBody>
          </Modal>
        </div>
      ) : undefined}
    </>
  );
}

export default ComprasDirectasCreate;
